// src/components/Public/PropertyDetails.js - Full SEO Enhanced Version
import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { db } from '../../firebase';
import { doc, getDoc, collection, query, where, getDocs, orderBy, limit } from 'firebase/firestore';
import { FaWhatsapp, FaBed, FaBath, FaCar, FaMapMarkerAlt, FaRulerCombined, FaCompass, FaTag, FaInfoCircle, FaArrowLeft, FaArrowRight, FaHome } from 'react-icons/fa';

function PropertyDetails() {
  const { id } = useParams();
  const [property, setProperty] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [prevProperty, setPrevProperty] = useState(null);
  const [nextProperty, setNextProperty] = useState(null);
  const [relatedProperties, setRelatedProperties] = useState([]);

  useEffect(() => {
    if (!id) {
      setError("No property ID or slug provided.");
      setLoading(false);
      return;
    }

    const fetchPropertyDetails = async () => {
      try {
        setLoading(true);
        
        // First try to get by document ID (for existing URLs)
        const docRef = doc(db, 'properties', id);
        const docSnap = await getDoc(docRef);

        let currentProperty = null;

        if (docSnap.exists()) {
          currentProperty = { id: docSnap.id, ...docSnap.data() };
          setProperty(currentProperty);
        } else {
          // If not found by ID, try to find by slug
          const slugQuery = query(
            collection(db, 'properties'), 
            where('slug', '==', id)
          );
          const slugSnapshot = await getDocs(slugQuery);
          
          if (!slugSnapshot.empty) {
            const doc = slugSnapshot.docs[0];
            currentProperty = { id: doc.id, ...doc.data() };
            setProperty(currentProperty);
          } else {
            setError("Property not found.");
            return;
          }
        }

        // Fetch adjacent and related properties
        if (currentProperty) {
          await Promise.all([
            fetchAdjacentProperties(currentProperty),
            fetchRelatedProperties(currentProperty)
          ]);
        }

      } catch (err) {
        console.error("Error fetching property details:", err);
        setError("Failed to load property details. Please try again later.");
      } finally {
        setLoading(false);
      }
    };

    fetchPropertyDetails();
  }, [id]);

  const fetchAdjacentProperties = async (currentProperty) => {
    try {
      // Use date_status for ordering, fallback to a default date if not available
      const currentDate = currentProperty.date_status || new Date();

      // Get previous property (older)
      const prevQuery = query(
        collection(db, 'properties'),
        where('status', '==', 'Available'),
        orderBy('date_status', 'desc'),
        limit(50) // Get more to filter properly
      );

      // Get next property (newer) 
      const nextQuery = query(
        collection(db, 'properties'),
        where('status', '==', 'Available'),
        orderBy('date_status', 'asc'),
        limit(50) // Get more to filter properly
      );

      const [allPropertiesDesc, allPropertiesAsc] = await Promise.all([
        getDocs(prevQuery),
        getDocs(nextQuery)
      ]);

      // Convert to arrays and find current property index
      const propertiesDesc = allPropertiesDesc.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      const propertiesAsc = allPropertiesAsc.docs.map(doc => ({ id: doc.id, ...doc.data() }));

      // Find previous property (next in desc order after current)
      const currentIndexDesc = propertiesDesc.findIndex(prop => prop.id === currentProperty.id);
      if (currentIndexDesc !== -1 && currentIndexDesc < propertiesDesc.length - 1) {
        setPrevProperty(propertiesDesc[currentIndexDesc + 1]);
      }

      // Find next property (next in asc order after current)
      const currentIndexAsc = propertiesAsc.findIndex(prop => prop.id === currentProperty.id);
      if (currentIndexAsc !== -1 && currentIndexAsc < propertiesAsc.length - 1) {
        setNextProperty(propertiesAsc[currentIndexAsc + 1]);
      }

    } catch (error) {
      console.error('Error fetching adjacent properties:', error);
    }
  };

  const fetchRelatedProperties = async (currentProperty) => {
    try {
      // Find properties in the same location
      const relatedQuery = query(
        collection(db, 'properties'),
        where('status', '==', 'Available'),
        where('location', '==', currentProperty.location),
        orderBy('date_status', 'desc'),
        limit(4)
      );

      const relatedSnapshot = await getDocs(relatedQuery);
      const related = relatedSnapshot.docs
        .map(doc => ({ id: doc.id, ...doc.data() }))
        .filter(prop => prop.id !== currentProperty.id); // Exclude current property

      setRelatedProperties(related);
    } catch (error) {
      console.error('Error fetching related properties:', error);
      // If location query fails, try without location filter
      try {
        const fallbackQuery = query(
          collection(db, 'properties'),
          where('status', '==', 'Available'),
          orderBy('date_status', 'desc'),
          limit(4)
        );
        const fallbackSnapshot = await getDocs(fallbackQuery);
        const fallback = fallbackSnapshot.docs
          .map(doc => ({ id: doc.id, ...doc.data() }))
          .filter(prop => prop.id !== currentProperty.id);
        setRelatedProperties(fallback);
      } catch (fallbackError) {
        console.error('Error fetching fallback related properties:', fallbackError);
      }
    }
  };

  // Gallery Navigation Functions
  const goToNextImage = () => {
    if (property && property.images && property.images.length > 0) {
      setCurrentImageIndex((prevIndex) => (prevIndex + 1) % property.images.length);
    }
  };

  const goToPrevImage = () => {
    if (property && property.images && property.images.length > 0) {
      setCurrentImageIndex((prevIndex) => (prevIndex - 1 + property.images.length) % property.images.length);
    }
  };

  // SEO and meta information
  const generateMetaDescription = (property) => {
    const parts = [
      property.title,
      `in ${property.location}`,
      property.bedrooms ? `${property.bedrooms} bedrooms` : '',
      property.bathrooms ? `${property.bathrooms} bathrooms` : '',
      property.price_sale ? `For sale from RM ${property.price_sale.toLocaleString()}` : '',
      property.price_rent ? `For rent from RM ${property.price_rent.toLocaleString()}/month` : ''
    ].filter(Boolean);
    
    return parts.join('. ') + '.';
  };

  const agentName = "Kay Hooy";
  const agentContact = "0135339349";
  const whatsappMessage = encodeURIComponent(`Hi ${agentName}, I am interested in property *ID: ${property?.display_id || ''}.* Is it still available?`);

  if (loading) return <div style={loadingStyle}>Loading...</div>;
  if (error) return <div style={errorStyle}>{error}</div>;
  if (!property) return <div style={errorStyle}>Property not found.</div>;

  const features = [
    { label: 'Bedrooms', value: property.bedrooms, icon: <FaBed /> },
    { label: 'Bathrooms', value: property.bathrooms, icon: <FaBath /> },
    { label: 'Carparks', value: property.carparks, icon: <FaCar /> },
    { label: 'Built-up Area', value: property.built_up_area ? `${property.built_up_area} sqft` : null, icon: <FaRulerCombined /> },
    { label: 'Land Area', value: property.land_area ? `${property.land_area} sqft` : null, icon: <FaCompass /> },
    { label: 'Property Type', value: property.property_type, icon: <FaInfoCircle /> },
  ].filter(f => f.value);

  return (
    <>
      {/* SEO Meta Tags */}
      <Helmet>
        <title>{property.title} - {property.location} | Property Listings</title>
        <meta name="description" content={generateMetaDescription(property)} />
        <meta property="og:title" content={`${property.title} - ${property.location}`} />
        <meta property="og:description" content={generateMetaDescription(property)} />
        <meta property="og:image" content={property.images?.[0]} />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={`${window.location.origin}/listing/${property.slug || property.id}`} />
        <link rel="canonical" href={`${window.location.origin}/listing/${property.slug || property.id}`} />
        
        {/* Schema.org structured data */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "http://schema.org",
            "@type": "RealEstateListing",
            "name": property.title,
            "description": generateMetaDescription(property),
            "url": `${window.location.origin}/listing/${property.slug || property.id}`,
            "image": property.images,
            "address": {
              "@type": "PostalAddress",
              "addressLocality": property.location
            },
            "floorSize": {
              "@type": "QuantitativeValue",
              "value": property.built_up_area,
              "unitCode": "SQF"
            },
            "numberOfRooms": property.bedrooms,
            "numberOfBathroomsTotal": property.bathrooms,
            "offers": {
              "@type": "Offer",
              "price": property.price_sale || property.price_rent,
              "priceCurrency": "MYR"
            }
          })}
        </script>
      </Helmet>

      <div style={containerStyle}>
        {/* Breadcrumb Navigation */}
        <nav style={breadcrumbStyle}>
          <Link to="/" style={breadcrumbLinkStyle}>
            <FaHome style={{ marginRight: '5px' }} />
            Home
          </Link>
          <span style={breadcrumbSeparatorStyle}> {'>'} </span>
          <Link to="/" style={breadcrumbLinkStyle}>Properties</Link>
          <span style={breadcrumbSeparatorStyle}> {'>'} </span>
          <span style={breadcrumbCurrentStyle}>{property.title}</span>
        </nav>

        {/* Image Gallery Section */}
        <div style={galleryContainerStyle}>
          {property.images && property.images.length > 0 ? (
            <>
              <img
                src={property.images[currentImageIndex]}
                alt={`${property.title} ${currentImageIndex + 1}`}
                style={galleryMainImageStyle}
              />
              {property.images.length > 1 && (
                <>
                  <button onClick={goToPrevImage} style={{ ...galleryNavButtonStyle, left: '10px' }}>&#10094;</button>
                  <button onClick={goToNextImage} style={{ ...galleryNavButtonStyle, right: '10px' }}>&#10095;</button>
                </>
              )}
              <div style={thumbnailStripStyle}>
                {property.images.map((image, index) => (
                  <img
                    key={index}
                    src={image}
                    alt={`Thumbnail ${index + 1}`}
                    style={{ ...thumbnailStyle, border: index === currentImageIndex ? '2px solid #007bff' : '2px solid transparent' }}
                    onClick={() => setCurrentImageIndex(index)}
                  />
                ))}
              </div>
              <p style={imageCountStyle}>{currentImageIndex + 1} / {property.images.length}</p>
            </>
          ) : (
            <p style={noImageStyle}>No images available for this property.</p>
          )}
        </div>

        <div style={contentContainerStyle}>
          <div style={headerStyle}>
            <h1 style={titleStyle}>{property.title}</h1>
            <p style={locationStyle}><FaMapMarkerAlt style={{ marginRight: '8px' }} />{property.location}</p>
            {property.display_id && (
              <p style={propertyIdStyle}>Property ID: {property.display_id}</p>
            )}
          </div>

          <div style={priceContainerStyle}>
            {property.listing_type && property.listing_type.includes('sale') && property.price_sale && (
              <div style={priceBlockStyle}>
                <span style={priceLabelStyle}><FaTag style={{ marginRight: '5px' }} />For Sale</span>
                <span style={priceValueStyle}>RM {Number(property.price_sale).toLocaleString()}</span>
              </div>
            )}
            {property.listing_type && property.listing_type.includes('rent') && property.price_rent && (
              <div style={priceBlockStyle}>
                <span style={priceLabelStyle}><FaTag style={{ marginRight: '5px' }} />For Rent</span>
                <span style={priceValueStyle}>RM {Number(property.price_rent).toLocaleString()} / month</span>
              </div>
            )}
          </div>
          
          <div style={detailsGridStyle}>
            {features.map((feature, index) => (
              <div key={index} style={detailItemStyle}>
                {feature.icon}
                <div style={detailTextContainerStyle}>
                  <span style={detailLabelStyle}>{feature.label}</span>
                  <span style={detailValueStyle}>{feature.value}</span>
                </div>
              </div>
            ))}
          </div>

          <h2 style={sectionHeadingStyle}>Description</h2>
          <div 
            style={descriptionStyle}
            dangerouslySetInnerHTML={{ __html: property.description }} 
          />

          {/* Agent Contact Section */}
          <div style={agentContactContainerStyle}>
            <h3 style={agentNameStyle}>{agentName}</h3>
            <p>Your property consultant</p>
            <a
              href={`https://wa.me/6${agentContact}?text=${whatsappMessage}`}
              style={whatsappButtonStyle}
              target="_blank"
              rel="noopener noreferrer"
            >
              <FaWhatsapp style={{ marginRight: '10px', fontSize: '1.5em' }} />
              WhatsApp Now
            </a>
          </div>

          {/* Related Properties Section */}
          {relatedProperties.length > 0 && (
            <div style={relatedSectionStyle}>
              <h3 style={sectionHeadingStyle}>Similar Properties in {property.location}</h3>
              <div style={relatedGridStyle}>
                {relatedProperties.slice(0, 3).map(relatedProperty => (
                  <Link 
                    key={relatedProperty.id} 
                    to={`/listing/${relatedProperty.slug || relatedProperty.id}`}
                    style={relatedCardLinkStyle}
                  >
                    <div style={relatedCardStyle}>
                      <img 
                        src={relatedProperty.images?.[0] || '/placeholder.jpg'} 
                        alt={relatedProperty.title}
                        style={relatedImageStyle}
                      />
                      <div style={relatedContentStyle}>
                        <h4 style={relatedTitleStyle}>{relatedProperty.title}</h4>
                        <p style={relatedLocationStyle}>{relatedProperty.location}</p>
                        <p style={relatedPriceStyle}>
                          RM {(relatedProperty.price_sale || relatedProperty.price_rent)?.toLocaleString()}
                          {relatedProperty.price_rent ? '/month' : ''}
                        </p>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          )}

          {/* Previous/Next Navigation */}
          <div style={navigationContainerStyle}>
            {prevProperty && (
              <Link to={`/listing/${prevProperty.slug || prevProperty.id}`} style={navLinkStyle}>
                <div style={navItemStyle}>
                  <FaArrowLeft style={{ marginRight: '8px' }} />
                  <div>
                    <span style={navLabelStyle}>Previous Property</span>
                    <span style={navTitleStyle}>{prevProperty.title}</span>
                  </div>
                </div>
              </Link>
            )}
            
            <div style={navCenterStyle}>
              <Link to="/" style={backToListingsStyle}>
                Back to All Properties
              </Link>
            </div>

            {nextProperty && (
              <Link to={`/listing/${nextProperty.slug || nextProperty.id}`} style={navLinkStyle}>
                <div style={navItemRightStyle}>
                  <div>
                    <span style={navLabelStyle}>Next Property</span>
                    <span style={navTitleStyle}>{nextProperty.title}</span>
                  </div>
                  <FaArrowRight style={{ marginLeft: '8px' }} />
                </div>
              </Link>
            )}
          </div>
        </div>
      </div>
    </>
  );
}

// --- Enhanced Styles ---
const breadcrumbStyle = {
  padding: '10px 0',
  marginBottom: '20px',
  fontSize: '0.9em',
  color: '#666',
};

const breadcrumbLinkStyle = {
  color: '#007bff',
  textDecoration: 'none',
  display: 'inline-flex',
  alignItems: 'center',
};

const breadcrumbSeparatorStyle = {
  margin: '0 8px',
  color: '#999',
};

const breadcrumbCurrentStyle = {
  color: '#333',
  fontWeight: 'bold',
};

const propertyIdStyle = {
  fontSize: '0.9em',
  color: '#888',
  margin: '5px 0',
};

const navigationContainerStyle = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  marginTop: '40px',
  padding: '20px 0',
  borderTop: '2px solid #e9ecef',
  gap: '20px',
  flexWrap: 'wrap',
};

const navLinkStyle = {
  textDecoration: 'none',
  color: 'inherit',
  flex: '1',
  maxWidth: '300px',
  minWidth: '200px',
};

const navItemStyle = {
  display: 'flex',
  alignItems: 'center',
  padding: '15px',
  backgroundColor: '#f8f9fa',
  borderRadius: '8px',
  border: '1px solid #dee2e6',
  transition: 'all 0.3s ease',
  cursor: 'pointer',
};

const navItemRightStyle = {
  ...navItemStyle,
  flexDirection: 'row-reverse',
  textAlign: 'right',
};

const navCenterStyle = {
  flex: '0 0 auto',
};

const backToListingsStyle = {
  padding: '10px 20px',
  backgroundColor: '#007bff',
  color: 'white',
  textDecoration: 'none',
  borderRadius: '5px',
  fontWeight: 'bold',
};

const navLabelStyle = {
  display: 'block',
  fontSize: '0.8em',
  color: '#666',
  marginBottom: '5px',
};

const navTitleStyle = {
  display: 'block',
  fontSize: '0.9em',
  fontWeight: 'bold',
  color: '#333',
};

const relatedSectionStyle = {
  marginTop: '40px',
  padding: '20px 0',
  borderTop: '2px solid #e9ecef',
};

const relatedGridStyle = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
  gap: '20px',
  marginTop: '20px',
};

const relatedCardLinkStyle = {
  textDecoration: 'none',
  color: 'inherit',
};

const relatedCardStyle = {
  backgroundColor: '#fff',
  borderRadius: '8px',
  boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
  overflow: 'hidden',
  transition: 'transform 0.2s ease',
  cursor: 'pointer',
};

const relatedImageStyle = {
  width: '100%',
  height: '150px',
  objectFit: 'cover',
};

const relatedContentStyle = {
  padding: '15px',
};

const relatedTitleStyle = {
  fontSize: '1em',
  fontWeight: 'bold',
  margin: '0 0 5px 0',
  color: '#333',
};

const relatedLocationStyle = {
  fontSize: '0.9em',
  color: '#666',
  margin: '0 0 10px 0',
};

const relatedPriceStyle = {
  fontSize: '1em',
  fontWeight: 'bold',
  color: '#007bff',
  margin: '0',
};

// --- Original Styles (keeping existing ones) ---
const loadingStyle = { textAlign: 'center', fontSize: '1.5em', marginTop: '50px' };
const errorStyle = { textAlign: 'center', color: 'red', fontSize: '1.2em', marginTop: '50px' };
const containerStyle = {
  maxWidth: '900px',
  margin: '20px auto',
  padding: '20px',
  backgroundColor: '#fff',
  borderRadius: '10px',
  boxShadow: '0 4px 15px rgba(0,0,0,0.1)',
  fontFamily: 'Arial, sans-serif',
};

const galleryContainerStyle = {
  position: 'relative',
  width: '100%',
  borderRadius: '10px',
  marginBottom: '20px',
  backgroundColor: '#eee',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  padding: '10px 0',
};

const galleryMainImageStyle = {
  width: '100%',
  height: 'auto',
  maxHeight: '450px',
  objectFit: 'contain',
  borderRadius: '10px',
  backgroundColor: '#eee',
};

const galleryNavButtonStyle = {
  position: 'absolute',
  top: '50%',
  transform: 'translateY(-50%)',
  backgroundColor: 'rgba(0,0,0,0.5)',
  color: 'white',
  border: 'none',
  borderRadius: '50%',
  width: '40px',
  height: '40px',
  fontSize: '24px',
  cursor: 'pointer',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  zIndex: 10,
};

const thumbnailStripStyle = {
  display: 'flex',
  overflowX: 'auto',
  gap: '10px',
  padding: '10px 0',
  justifyContent: 'center',
  width: '100%',
  marginBottom: '10px',
};

const thumbnailStyle = {
  width: '80px',
  height: '80px',
  objectFit: 'cover',
  borderRadius: '5px',
  cursor: 'pointer',
  transition: 'border 0.2s ease',
};

const imageCountStyle = {
  position: 'absolute',
  bottom: '10px',
  right: '10px',
  backgroundColor: 'rgba(0,0,0,0.5)',
  color: 'white',
  padding: '5px 10px',
  borderRadius: '5px',
  fontSize: '0.9em',
};

const noImageStyle = {
  position: 'absolute',
  top: 0,
  left: 0,
  width: '100%',
  height: '100%',
  backgroundColor: '#f0f0f0',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  fontSize: '1.2em',
  color: '#888',
};

const contentContainerStyle = {
  marginTop: '20px',
  padding: '0 10px',
};

const headerStyle = {
  borderBottom: '2px solid #e9ecef',
  paddingBottom: '15px',
  marginBottom: '20px',
};

const titleStyle = {
  fontSize: '2em',
  fontWeight: 'bold',
  color: '#333',
  margin: '0',
};

const locationStyle = {
  fontSize: '1.1em',
  color: '#777',
  marginTop: '5px',
  display: 'flex',
  alignItems: 'center',
};

const detailsGridStyle = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
  gap: '15px',
  marginBottom: '30px',
};

const detailItemStyle = {
  backgroundColor: '#f8f9fa',
  padding: '15px',
  borderRadius: '8px',
  display: 'flex',
  alignItems: 'center',
  gap: '12px',
};

const detailTextContainerStyle = {
  display: 'flex',
  flexDirection: 'column',
};

const detailLabelStyle = {
  fontSize: '0.9em',
  color: '#888',
};

const detailValueStyle = {
  fontSize: '1.1em',
  fontWeight: 'bold',
  color: '#333',
};

const sectionHeadingStyle = {
  fontSize: '1.5em',
  color: '#333',
  borderBottom: '2px solid #e9ecef',
  paddingBottom: '10px',
  marginBottom: '15px',
};

const priceContainerStyle = {
  backgroundColor: '#f8f9fa',
  borderRadius: '10px',
  padding: '20px',
  marginBottom: '25px',
  display: 'flex',
  flexWrap: 'wrap',
  justifyContent: 'space-around',
  alignItems: 'center',
};

const priceBlockStyle = {
  textAlign: 'center',
  marginTop: '10px',
  marginBottom: '10px',
};

const priceLabelStyle = {
  fontSize: '1.2em',
  fontWeight: 'normal',
  color: '#000',
  marginBottom: '5px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
};

const priceValueStyle = {
  fontSize: '1.8em',
  fontWeight: 'bold',
  color: '#28a745',
};

const descriptionStyle = {
  fontSize: '1.1em',
  lineHeight: '1.6',
  color: '#555',
  marginBottom: '30px',
};

const agentContactContainerStyle = {
  textAlign: 'center',
  marginTop: '30px',
  padding: '20px',
  backgroundColor: '#e6f7ff',
  borderRadius: '10px',
  border: '1px solid #cceeff',
};

const agentNameStyle = {
  fontSize: '1.3em',
  fontWeight: 'bold',
  color: '#333',
  marginBottom: '15px',
};

const whatsappButtonStyle = {
  display: 'inline-flex',
  alignItems: 'center',
  backgroundColor: '#25D366',
  color: 'white',
  padding: '12px 25px',
  borderRadius: '30px',
  textDecoration: 'none',
  fontSize: '1.2em',
  fontWeight: 'bold',
  boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
};

export default PropertyDetails;